import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Package, Boxes, Truck, MapPinned, CheckCircle2, Copy, Check,
  ExternalLink, XCircle, RefreshCw, ShoppingBag, MapPin, CalendarClock, Search,
} from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

// Mirrors the auth pattern used across this codebase (Header.js / UserDashboard.js
// / InstantOrderTracking.js) — copy-pasted per-file rather than imported, per
// established convention.
const getSafeLoggedInUser = () => {
  const raw = localStorage.getItem("currentUser");
  const token = localStorage.getItem("authToken") || "";
  if (!raw && !token) return null;
  try { return { ...JSON.parse(raw || "{}"), token }; } catch { return { token }; }
};
const getAuthMobile = () => {
  const u = getSafeLoggedInUser();
  return u?.mobile || u?.phone || u?.mobile_number || "";
};
const getAuthToken = () =>
  localStorage.getItem("authToken") || localStorage.getItem("accessToken") ||
  localStorage.getItem("access_token") || localStorage.getItem("token") || "";
const isUserLoggedIn = () => {
  const u = getSafeLoggedInUser();
  return !!(u?.token || u?.id || u?.mobile || u?.phone);
};
const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

// Delivery lifecycle for shopping-cart product orders — separate from the
// service-visit ORDER_STATUS codes used for bookings (see backend
// PRODUCT_DELIVERY_STATUS in views.py). 5 = Cancelled is a terminal state
// rendered on its own, not as a stepper position.
const DELIVERY_STEPS = [
  { label: "Processing",       icon: Package,    desc: "We've received your order and are getting it ready." },
  { label: "Packed",           icon: Boxes,      desc: "Your order has been packed and is ready for pickup." },
  { label: "Shipped",          icon: Truck,      desc: "Your order is on its way to you." },
  { label: "Out for Delivery", icon: MapPinned,  desc: "Your order is out for delivery today." },
  { label: "Delivered",        icon: CheckCircle2, desc: "Your order has been delivered." },
];

const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const TrackOrderPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedFor, setCopiedFor] = useState("");
  const [search, setSearch] = useState("");

  const loadOrders = async () => {
    setLoading(true);
    setError("");
    try {
      const mobile = getAuthMobile();
      const res = await fetch(`${API_BASE_URL}/api/orders/user/product/${encodeURIComponent(mobile)}/`, {
        credentials: "include",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Unable to load your orders.");
      setOrders(Array.isArray(data?.orders) ? data.orders : []);
    } catch (e) {
      setError(e?.message || "Something went wrong while loading your orders.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isUserLoggedIn()) {
      navigate("/user/user-login", { state: { redirectTo: "/track-order" } });
      return;
    }
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyTracking = (orderId, trackingNumber) => {
    if (!trackingNumber) return;
    navigator.clipboard?.writeText(trackingNumber).then(() => {
      setCopiedFor(orderId);
      setTimeout(() => setCopiedFor(""), 1800);
    });
  };

  // Only filters once the customer actually types something — an empty
  // search shows every order, same as before this was added.
  const query = search.trim().toLowerCase();
  const filteredOrders = useMemo(() => {
    if (!query) return orders;
    return orders.filter((order) => {
      const inOrderId = String(order.order_id || "").toLowerCase().includes(query);
      const inProducts = (order.products || []).some((p) =>
        String(p.name || "").toLowerCase().includes(query)
      );
      const inTracking = String(order.delivery?.tracking_number || "").toLowerCase().includes(query);
      return inOrderId || inProducts || inTracking;
    });
  }, [orders, query]);

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.hero}>
          <div style={S.heroBadge}><Package size={13} /> TRACK ORDER</div>
          <h1 style={S.heroTitle}>Track Your Orders</h1>
          <p style={S.heroSub}>Check the delivery status of products you've bought from Shopping</p>
        </div>

        {loading && (
          <div style={S.stateBox}>
            <RefreshCw size={22} color="#4f46e5" style={S.spin} />
            <p style={S.stateText}>Loading your orders…</p>
          </div>
        )}

        {!loading && error && (
          <div style={S.errorBox}>
            <p style={S.errorText}>{error}</p>
            <button type="button" style={S.retryBtn} onClick={loadOrders}>
              <RefreshCw size={14} /> Try Again
            </button>
          </div>
        )}

        {!loading && !error && orders.length === 0 && (
          <div style={S.emptyBox}>
            <ShoppingBag size={30} color="#4f46e5" />
            <h3 style={S.emptyTitle}>No orders yet</h3>
            <p style={S.emptyText}>Products you order from Shopping will show up here with live tracking.</p>
            <Link to="/shopping" style={S.shopBtnLink}>
              <button type="button" style={S.shopBtn}>Start Shopping</button>
            </Link>
          </div>
        )}

        {!loading && !error && orders.length > 0 && (
          <div style={S.searchBar}>
            <Search size={16} color="#9ca3af" />
            <input
              type="text"
              placeholder="Search by order ID or product name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={S.searchInput}
            />
          </div>
        )}

        {!loading && !error && orders.length > 0 && query && filteredOrders.length === 0 && (
          <div style={S.emptyBox}>
            <Search size={28} color="#94a3b8" />
            <h3 style={S.emptyTitle}>No orders found</h3>
            <p style={S.emptyText}>We couldn't find any order matching "{search.trim()}".</p>
          </div>
        )}

        {!loading && !error && filteredOrders.length > 0 && (
          <div style={S.orderList}>
            {filteredOrders.map((order) => {
              const delivery = order.delivery || {};
              const status = typeof delivery.status === "number" ? delivery.status : 0;
              const isCancelled = status === 5;
              const addr = order.delivery_address || {};
              const hasTracking = status >= 2 && (delivery.courier_name || delivery.tracking_number);

              return (
                <div key={order._id || order.order_id} style={S.orderCard}>
                  <div style={S.orderHead}>
                    <div>
                      <div style={S.orderIdRow}>
                        <span style={S.orderId}>Order #{order.order_id}</span>
                        {isCancelled && (
                          <span style={S.cancelledBadge}><XCircle size={12} /> Cancelled</span>
                        )}
                      </div>
                      <div style={S.orderMeta}>Placed on {formatDate(order.created_at)}</div>
                    </div>
                    <div style={S.orderTotal}>₹{Number(order.products_subtotal || 0).toFixed(2)}</div>
                  </div>

                  <div style={S.productsRow}>
                    {(order.products || []).map((p, i) => (
                      <div key={i} style={S.productChip}>
                        {p.image ? (
                          <img src={p.image} alt={p.name} style={S.productImg} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        ) : (
                          <div style={S.productImgFallback}><ShoppingBag size={14} color="#a5b4fc" /></div>
                        )}
                        <span style={S.productName}>{p.name} × {p.qty}</span>
                      </div>
                    ))}
                  </div>

                  {!isCancelled && (
                    <div style={S.stepper}>
                      {DELIVERY_STEPS.map((step, i) => {
                        const Icon = step.icon;
                        const done = i < status;
                        const active = i === status;
                        return (
                          <React.Fragment key={step.label}>
                            <div style={S.stepItem}>
                              <div style={{ ...S.stepDot, ...(done ? S.stepDotDone : {}), ...(active ? S.stepDotActive : {}) }}>
                                {done ? <CheckCircle2 size={14} /> : <Icon size={13} />}
                              </div>
                              <span style={{ ...S.stepLabel, ...(active || done ? S.stepLabelActive : {}) }}>{step.label}</span>
                            </div>
                            {i < DELIVERY_STEPS.length - 1 && (
                              <div style={{ ...S.stepConnector, ...(i < status ? S.stepConnectorActive : {}) }} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  )}

                  {!isCancelled && (
                    <div style={S.statusNote}>
                      {DELIVERY_STEPS[Math.min(status, 4)]?.desc}
                    </div>
                  )}

                  {isCancelled && (
                    <div style={S.cancelledNote}>
                      This order was cancelled{delivery.cancelled_at ? ` on ${formatDate(delivery.cancelled_at)}` : ""}.
                    </div>
                  )}

                  {hasTracking && !isCancelled && (
                    <div style={S.trackingBox}>
                      {delivery.courier_name && (
                        <div style={S.trackingRow}><span style={S.trackingLabel}>Courier</span><span style={S.trackingValue}>{delivery.courier_name}</span></div>
                      )}
                      {delivery.tracking_number && (
                        <div style={S.trackingRow}>
                          <span style={S.trackingLabel}>Tracking No.</span>
                          <span style={S.trackingValueRow}>
                            {delivery.tracking_number}
                            <button type="button" style={S.copyBtn} onClick={() => copyTracking(order.order_id, delivery.tracking_number)}>
                              {copiedFor === order.order_id ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}
                            </button>
                          </span>
                        </div>
                      )}
                      {delivery.estimated_delivery && (
                        <div style={S.trackingRow}>
                          <span style={S.trackingLabel}><CalendarClock size={12} style={S.inlineIcon} /> Est. Delivery</span>
                          <span style={S.trackingValue}>{delivery.estimated_delivery}</span>
                        </div>
                      )}
                      {delivery.tracking_url && (
                        <a href={delivery.tracking_url} target="_blank" rel="noopener noreferrer" style={S.trackLinkBtn}>
                          Track on Courier Site <ExternalLink size={13} />
                        </a>
                      )}
                    </div>
                  )}

                  {(addr.line1 || addr.city) && (
                    <div style={S.addressRow}>
                      <MapPin size={14} color="#9ca3af" style={S.inlineIcon} />
                      <span>{[addr.line1, addr.area, addr.city, addr.pincode].filter(Boolean).join(", ")}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loading && !error && (
          <div style={S.helpBar}>
            Need help with an order? <Link to="/contact-us" style={S.helpLink}>Contact Us</Link>
          </div>
        )}
      </div>
    </div>
  );
};

const S = {
  page:      { background: "#f8fafc", minHeight: "100vh", padding: "32px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "760px", margin: "0 auto" },

  hero:      { textAlign: "center", marginBottom: "28px" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#eef2ff", color: "#4338ca", fontSize: "11px", fontWeight: 800, padding: "6px 12px", borderRadius: "999px", marginBottom: "12px" },
  heroTitle: { margin: 0, fontSize: "26px", fontWeight: 900, letterSpacing: "-0.5px", color: "#111827" },
  heroSub:   { margin: "8px 0 0", fontSize: "14px", color: "#64748b" },

  searchBar:   { display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "13px 16px", marginBottom: "18px" },
  searchInput: { flex: 1, border: "none", outline: "none", fontSize: "14px", color: "#111827", background: "transparent", fontFamily: "inherit" },

  stateBox: { textAlign: "center", padding: "60px 20px", color: "#64748b" },
  stateText: { marginTop: "12px", fontSize: "14px" },
  spin: { animation: "spin 1s linear infinite" },

  errorBox: { background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "16px", padding: "24px", textAlign: "center" },
  errorText: { margin: "0 0 14px", color: "#b91c1c", fontSize: "14px" },
  retryBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff", border: "1px solid #fecaca", color: "#b91c1c", fontWeight: 700, fontSize: "13px", padding: "9px 16px", borderRadius: "10px", cursor: "pointer" },

  emptyBox: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "22px", padding: "48px 24px", textAlign: "center" },
  emptyTitle: { margin: "14px 0 6px", fontSize: "19px", fontWeight: 800, color: "#111827" },
  emptyText: { margin: "0 0 20px", fontSize: "14px", color: "#64748b" },
  shopBtnLink: { textDecoration: "none" },
  shopBtn: { background: "linear-gradient(90deg,#4f46e5,#4338ca)", color: "#fff", border: "none", borderRadius: "12px", padding: "13px 26px", fontSize: "14px", fontWeight: 800, cursor: "pointer" },

  orderList: { display: "flex", flexDirection: "column", gap: "16px" },
  orderCard: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "22px", padding: "20px", boxShadow: "0 10px 30px rgba(15,23,42,0.05)" },
  orderHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", marginBottom: "14px" },
  orderIdRow: { display: "flex", alignItems: "center", gap: "8px" },
  orderId: { fontSize: "14.5px", fontWeight: 800, color: "#111827" },
  orderMeta: { fontSize: "12px", color: "#94a3b8", marginTop: "3px" },
  orderTotal: { fontSize: "16px", fontWeight: 900, color: "#111827" },
  cancelledBadge: { display: "inline-flex", alignItems: "center", gap: "4px", background: "#fef2f2", color: "#b91c1c", fontSize: "11px", fontWeight: 800, padding: "3px 9px", borderRadius: "999px" },

  productsRow: { display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" },
  productChip: { display: "flex", alignItems: "center", gap: "8px", background: "#f8fafc", border: "1px solid #eceef5", borderRadius: "10px", padding: "6px 10px 6px 6px" },
  productImg: { width: "26px", height: "26px", objectFit: "cover", borderRadius: "6px", background: "#eef2ff" },
  productImgFallback: { width: "26px", height: "26px", borderRadius: "6px", background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center" },
  productName: { fontSize: "12.5px", fontWeight: 600, color: "#374151" },

  stepper: { display: "flex", alignItems: "flex-start", marginBottom: "12px" },
  stepItem: { display: "flex", flexDirection: "column", alignItems: "center", width: "64px", flexShrink: 0 },
  stepDot: { width: "26px", height: "26px", borderRadius: "50%", background: "#e5e7eb", color: "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "6px" },
  stepDotActive: { background: "#eef2ff", color: "#4338ca", boxShadow: "0 0 0 3px rgba(79,70,229,0.15)" },
  stepDotDone: { background: "#4f46e5", color: "#fff" },
  stepLabel: { fontSize: "10px", fontWeight: 700, color: "#9ca3af", textAlign: "center", lineHeight: 1.2 },
  stepLabelActive: { color: "#111827" },
  stepConnector: { flex: 1, height: "2px", background: "#e5e7eb", marginTop: "13px" },
  stepConnectorActive: { background: "#4f46e5" },

  statusNote: { fontSize: "12.5px", color: "#64748b", marginBottom: "14px" },
  cancelledNote: { fontSize: "12.5px", color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 12px", marginBottom: "14px" },

  trackingBox: { background: "#f8fafc", border: "1px solid #eceef5", borderRadius: "14px", padding: "14px 16px", marginBottom: "14px" },
  trackingRow: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12.5px", padding: "4px 0" },
  trackingLabel: { color: "#64748b", fontWeight: 600, display: "inline-flex", alignItems: "center" },
  trackingValue: { color: "#111827", fontWeight: 700 },
  trackingValueRow: { color: "#111827", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: "8px" },
  copyBtn: { background: "none", border: "none", cursor: "pointer", padding: "2px", display: "flex", alignItems: "center", color: "#4f46e5" },
  trackLinkBtn: { display: "inline-flex", alignItems: "center", gap: "6px", marginTop: "8px", fontSize: "12.5px", fontWeight: 800, color: "#4338ca", textDecoration: "none" },
  inlineIcon: { marginRight: "4px", verticalAlign: "-2px" },

  addressRow: { display: "flex", alignItems: "flex-start", gap: "6px", fontSize: "12.5px", color: "#64748b", lineHeight: 1.5 },

  helpBar: { textAlign: "center", fontSize: "13px", color: "#64748b", marginTop: "28px" },
  helpLink: { color: "#4338ca", fontWeight: 700, textDecoration: "none" },
};

if (typeof document !== "undefined" && !document.getElementById("track-order-anim")) {
  const s = document.createElement("style");
  s.id = "track-order-anim";
  s.textContent = "@keyframes spin{to{transform:rotate(360deg)}}";
  document.head.appendChild(s);
}

export default TrackOrderPage;
